/**
 * THE ROLE DASHBOARDS (0173, the owner's brief §1). One front page per job, drawn from
 * `home.figures`: the CEO reads the company, the sales manager the pipeline, the accountant
 * the money, the operator a row of large actions. The branch manager keeps this morning's
 * KPI dashboard (`home.tsx`), which is also what a person with no business role sees.
 * Every figure is a link to the list it counts — a number nobody can open is decoration.
 */
import * as React from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, go } from '../hooks.js';
import { Banner, Card, Kpi, Loading } from '../ui.js';
import { Page } from '../shell.js';
import { count, greeting, longDate, money } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('home')!.messages;

type Figures = Awaited<ReturnType<typeof api.view<'home.figures'>>>;

function useFigures() { return useAsk(() => api.view('home.figures', {}), [], MESSAGES); }

function Frame({ title, sub, children, actions }: { title: string; sub?: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return <Page title={title} {...(sub ? { sub } : {})} {...(actions ? { actions } : {})}>{children}</Page>;
}

function Band({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="band">
      <h2 style={{ fontSize: 15, margin: '14px 0 8px' }}>{title}</h2>
      <div className="kpis">{children}</div>
    </section>
  );
}

type Health = Awaited<ReturnType<typeof api.view<'home.health'>>>;
type HealthGroup = Health['groups'][number];

/**
 * THE HEALTH BOARD — CTS1's, which the owner asked for by name.
 *
 * One score, then the checks that are NOT clear, each with the sentence that says what it
 * costs. The clear ones are counted, not drawn: a board that lists twelve green zeros is
 * the tile wall again in a different colour. Each role passes the groups its job owns, so
 * the operator is not scrolled past billing he cannot act on, and the owner sees all four.
 */
export function HealthBoard({ groups, title }: { groups: readonly HealthGroup[]; title?: string }) {
  const asked = useAsk(() => api.view('home.health', {}), [], MESSAGES);
  if (asked.loading) return <Card title={title ?? 'How the company is'}><Loading what="the checks" /></Card>;
  if (asked.error) return <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />;
  const h: Health = asked.data!;

  const mine = h.checks.filter((c) => groups.includes(c.group));
  const problems = mine.filter((c) => c.severity !== 'clear');
  const clear = mine.length - problems.length;
  // the score for THIS person's groups, not the company-wide one
  const score = mine.length === 0 ? 100 : Math.round((clear / mine.length) * 100);
  const tone = score >= 80 ? 'good' : score >= 50 ? 'warn' : 'error';

  return (
    <Card title={title ?? 'How the company is'}
          sub={problems.length === 0
            ? `all ${count(clear)} checks clear`
            : `${count(problems.length)} of ${count(mine.length)} checks need attention`}>
      <div className="row" style={{ alignItems: 'center', gap: 14, marginBottom: problems.length ? 12 : 0 }}>
        <strong className={`health-score ${tone}`}>{score}%</strong>
        <span className="muted" style={{ flex: 1 }}>
          {problems.length === 0
            ? 'Nothing on this board needs you. Cylinders out of test, money past its terms and price lists about to lapse would show here.'
            : 'Worst first. Every line says what it costs, and opens the list it counted.'}
        </span>
      </div>
      {problems
        .slice()
        .sort((a, b) => (a.severity === b.severity ? b.count - a.count : a.severity === 'danger' ? -1 : 1))
        .map((c) => (
          <a className="row-link" key={c.key} href={c.href}
             onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(c.href); }}>
            <span>
              <strong>{c.label}</strong>
              <span>{c.means}</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong className={`health-count ${c.severity}`}>{c.money ? money(c.count) : count(c.count)}</strong>
              <span className="chev" aria-hidden="true">&rsaquo;</span>
            </span>
          </a>
        ))}
    </Card>
  );
}

/**
 * A LINE THAT NEEDS SOMETHING DONE TO IT. The owner opened the executive screen and
 * called it horrible, and he was right: thirty-one tiles of equal weight, twenty-six
 * of them zero, and nothing saying which mattered. A zero is not news. These rows are
 * drawn ONLY when the number is not zero, so an empty list is the good answer and the
 * screen is as long as the day is bad.
 */
function Lines({ items }: { items: { label: string; value: string; note?: string; href: string; bad?: boolean }[] }) {
  return (
    <>
      {items.map((i) => (
        <a className="row-link" key={i.label} href={i.href}
           onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(i.href); }}>
          <span>
            <strong>{i.label}</strong>
            {i.note ? <span>{i.note}</span> : null}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: 18, ...(i.bad ? { color: 'var(--danger, #b3261e)' } : {}) }}>{i.value}</strong>
            <span className="chev" aria-hidden="true">&rsaquo;</span>
          </span>
        </a>
      ))}
    </>
  );
}

/**
 * CEO — three questions in the order an owner actually asks them: what needs me, where
 * is the money, and what is going wrong. Everything else is still here, one click away
 * and unchanged, because a figure somebody relied on must not vanish in a redesign.
 */
export function CeoHome({ session }: { session: Session }) {
  const asked = useFigures();
  if (asked.loading) return <Frame title="Executive overview"><Loading what="the company's figures" /></Frame>;
  if (asked.error) return <Frame title="Executive overview"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Frame>;
  const f: Figures = asked.data!;

  const decisions: { label: string; value: string; note?: string; href: string; bad?: boolean }[] = [];
  if (f.approvalsPending.shown) {
    if (f.approvalsPending.orders > 0) decisions.push({ label: 'Orders to approve', value: count(f.approvalsPending.orders), href: '#/approvals' });
    if (f.approvalsPending.loads > 0) decisions.push({ label: 'Loads to approve', value: count(f.approvalsPending.loads), note: 'prepared, not yet out of the gate', href: '#/approvals' });
    if (f.approvalsPending.purchases > 0) decisions.push({ label: 'Purchases to approve', value: count(f.approvalsPending.purchases), href: '#/approvals' });
    if (f.approvalsPending.holds > 0) decisions.push({ label: 'Quarantine releases', value: count(f.approvalsPending.holds), note: 'a vessel waits on your word', href: '#/approvals', bad: true });
  }

  const wrong: { label: string; value: string; note?: string; href: string; bad?: boolean }[] = [];
  if (f.cylinders.shown) {
    // FIRST, because D267 says an overdue vessel must not be filled. A safety matter
    // outranks money on any morning.
    if (f.cylinders.overdueTest > 0) wrong.push({ label: 'Test overdue', value: count(f.cylinders.overdueTest), note: 'must not be filled', href: '#/cylinders?test=overdue', bad: true });
    if (f.cylinders.quarantined > 0) wrong.push({ label: 'Held out of service', value: count(f.cylinders.quarantined), note: 'quarantined', href: '#/casework?queue=quarantine', bad: true });
    if (f.cylinders.unaccounted > 0) wrong.push({ label: 'Unaccounted cylinders', value: count(f.cylinders.unaccounted), note: 'nobody is recorded as holding them', href: '#/cylinders?state=unaccounted', bad: true });
    if (f.cylinders.overdueReturn > 0) wrong.push({ label: 'Overdue returns', value: count(f.cylinders.overdueReturn), note: 'out past 30 days', href: '#/billing' });
  }
  if (f.money.shown && f.money.overduePaise > 0) wrong.push({ label: 'Money past its terms', value: money(f.money.overduePaise), href: '#/billing', bad: true });
  if (f.pipeline.shown && f.pipeline.customersOnHold > 0) wrong.push({ label: 'Customers over their limit', value: count(f.pipeline.customersOnHold), href: '#/billing', bad: true });
  if (f.pipeline.shown && f.pipeline.tendersClosingSoon > 0) wrong.push({ label: 'Tenders closing within a week', value: count(f.pipeline.tendersClosingSoon), href: '#/tenders?live=1', bad: true });
  if (f.ops.shown && f.ops.damageToSettle > 0) wrong.push({ label: 'Damage to settle', value: count(f.ops.damageToSettle), href: '#/casework?queue=damage' });

  return (
    <Frame title="Executive overview" sub={`${greeting()} · ${longDate()}`}>
      <HealthBoard groups={['Tracking', 'Filling', 'Compliance', 'Billing']} />

      <div className="grid two" style={{ alignItems: 'start' }}>
        <Card title="Waiting on you" sub={decisions.length === 0 ? undefined : 'nothing moves until these are decided'}
              actions={decisions.length > 0 ? <a href="#/approvals" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/approvals'); }}>Approvals &rarr;</a> : undefined}>
          {decisions.length === 0
            ? <p className="muted">No decision is waiting on you.</p>
            : <Lines items={decisions} />}
        </Card>

        <Card title="Needs attention" sub={wrong.length === 0 ? undefined : 'only what is not as it should be'}>
          {wrong.length === 0
            ? <p className="muted">Nothing is wrong this morning. Overdue tests, quarantines, unaccounted cylinders and money past its terms would appear here.</p>
            : <Lines items={wrong} />}
        </Card>
      </div>

      {f.money.shown ? (
        <Band title="Money">
          <Kpi label="Outstanding" value={money(f.money.outstandingPaise)} tone="money" note={`${count(f.money.accountsOwing)} accounts`} href="#/billing" />
          <Kpi label="Collected this month" value={money(f.money.collectionsMonthPaise)} tone="good" note={`${money(f.money.collectionsTodayPaise)} today`} href="#/billing" />
          <Kpi label="Sales this month" value={money(f.money.salesMonthPaise)} tone="money" note="bills sent" href="#/reports" />
        </Band>
      ) : null}

      {/* EVERYTHING ELSE IS STILL HERE. A redesign that quietly drops a figure somebody
        * checked every morning has broken their job to tidy a screen. */}
      <details style={{ marginTop: 18 }}>
        <summary style={{ cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: '6px 0' }}>Every figure</summary>
        {f.money.shown ? (
          <Band title="Money">
            <Kpi label="Sales this month" value={money(f.money.salesMonthPaise)} tone="money" note="bills sent" href="#/reports" />
            <Kpi label="Collected today" value={money(f.money.collectionsTodayPaise)} tone="good" href="#/billing" />
            <Kpi label="Collected this month" value={money(f.money.collectionsMonthPaise)} tone="good" href="#/billing" />
            <Kpi label="Outstanding" value={money(f.money.outstandingPaise)} tone="money" note={`${count(f.money.accountsOwing)} accounts`} href="#/billing" />
            <Kpi label="Overdue" value={money(f.money.overduePaise)} tone={f.money.overduePaise > 0 ? 'danger' : 'empty'} note="past terms" href="#/billing" />
            <Kpi label="SD held" value={money(f.money.sdHeldPaise)} tone="empty" note="returnable" href="#/billing" />
          </Band>
        ) : null}
        {f.cylinders.shown ? (
          <Band title="Cylinders">
            <Kpi label="Owned" value={count(f.cylinders.owned)} href="#/cylinders" />
            <Kpi label="At branches" value={count(f.cylinders.atBranch)} tone="good" href="#/cylinders?holder=plant" />
            <Kpi label="With customers" value={count(f.cylinders.withCustomers)} tone="primary" href="#/cylinders?holder=customer" />
            <Kpi label="Overdue return" value={count(f.cylinders.overdueReturn)} tone={f.cylinders.overdueReturn > 0 ? 'danger' : 'empty'} note="out past 30 days" href="#/billing" />
            <Kpi label="Unaccounted" value={count(f.cylinders.unaccounted)} tone={f.cylinders.unaccounted > 0 ? 'danger' : 'empty'} href="#/cylinders?state=unaccounted" />
            <Kpi label="Customer-owned on site" value={count(f.cylinders.customerOwnedOnSite)} href="#/customers" />
            <Kpi label="Empty" value={count(f.cylinders.empty)} tone="empty" href="#/cylinders?state=empty_in_plant" />
            <Kpi label="Filled" value={count(f.cylinders.filled)} tone="good" href="#/cylinders?state=filled_in_plant" />
            <Kpi label="Not inspected" value={count(f.cylinders.notInspected)} tone={f.cylinders.notInspected > 0 ? 'money' : 'empty'} href="#/filling" />
            <Kpi label="Quarantined" value={count(f.cylinders.quarantined)} tone={f.cylinders.quarantined > 0 ? 'danger' : 'empty'} href="#/casework?queue=quarantine" />
            <Kpi label="In repair / testing" value={count(f.cylinders.inRepair + f.cylinders.inTesting)} href="#/cylinders" />
            <Kpi label="Test overdue" value={count(f.cylinders.overdueTest)} tone={f.cylinders.overdueTest > 0 ? 'danger' : 'empty'} note={`${count(f.cylinders.dueSoonTest)} due soon`} href="#/cylinders?test=overdue" />
          </Band>
        ) : null}
        {f.ops.shown ? (
          <Band title="Operations">
            <Kpi label="To fill" value={count(f.ops.toFill)} href="#/filling" />
            <Kpi label="Filled today" value={count(f.ops.filledToday)} tone="good" href="#/filling" />
            <Kpi label="Loads awaiting approval" value={count(f.ops.loadsAwaitingApproval)} tone="primary" href="#/approvals" />
            <Kpi label="Ready to depart" value={count(f.ops.toDepart)} href="#/departure" />
            <Kpi label="Out for delivery" value={count(f.ops.awaitingDelivery)} href="#/delivery" />
            <Kpi label="Damage to settle" value={count(f.ops.damageToSettle)} tone={f.ops.damageToSettle > 0 ? 'money' : 'empty'} href="#/casework?queue=damage" />
            <Kpi label="Area not recorded" value={count(f.ops.yardsUnrecorded)} href="#/yard" />
          </Band>
        ) : null}
        {f.pipeline.shown ? (
          <Band title="Pipeline">
            <Kpi label="Quotations issued" value={count(f.pipeline.quotesIssued)} note={`${count(f.pipeline.quotesDraft)} in draft`} href="#/quotes" />
            <Kpi label="Orders awaiting approval" value={count(f.pipeline.ordersAwaiting)} tone="primary" href="#/approvals" />
            <Kpi label="Orders open" value={count(f.pipeline.ordersOpen)} href="#/orders?outstanding=1" />
            <Kpi label="Tenders open" value={count(f.pipeline.tendersOpen)} {...(f.pipeline.tendersClosingSoon > 0 ? { note: `${count(f.pipeline.tendersClosingSoon)} close within a week`, tone: 'danger' as const } : {})} href="#/tenders?live=1" />
            <Kpi label="Purchases awaiting approval" value={count(f.pipeline.posAwaiting)} note={`${count(f.pipeline.posOpen)} open`} href="#/purchases" />
            <Kpi label="Customers over limit" value={count(f.pipeline.customersOnHold)} tone={f.pipeline.customersOnHold > 0 ? 'danger' : 'empty'} href="#/billing" />
          </Band>
        ) : null}
      </details>
      {can(session, 'audit.read') ? <p className="muted" style={{ marginTop: 12 }}>Every figure opens the list it counts. Reports carry the same figures by period and branch.</p> : null}
    </Frame>
  );
}

/** Sales manager — the pipeline, and the customers who need a word. */
export function SalesHome({ session }: { session: Session }) {
  const asked = useFigures();
  if (asked.loading) return <Frame title="Sales overview"><Loading what="the pipeline" /></Frame>;
  if (asked.error) return <Frame title="Sales overview"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Frame>;
  const f: Figures = asked.data!;
  const actions = (
    <>
      {can(session, 'customer.manage') ? <button className="btn primary" onClick={() => go('#/customers?new=1')}>New customer</button> : null}
      {can(session, 'quote.draft') ? <button className="btn" onClick={() => go('#/quotes?new=1')}>New quotation</button> : null}
      {can(session, 'order.create') ? <button className="btn" onClick={() => go('#/orders?new=1')}>New order</button> : null}
    </>
  );
  return (
    <Frame title="Sales overview" sub={`${greeting()} · ${longDate()}`} actions={actions}>
      <HealthBoard groups={['Billing']} title="What is costing us sales" />
      <Band title="Pipeline">
        <Kpi label="Quotations in draft" value={count(f.pipeline.quotesDraft)} tone="empty" href="#/quotes?status=draft" />
        <Kpi label="Quotations issued" value={count(f.pipeline.quotesIssued)} tone="primary" note="awaiting the customer" href="#/quotes?status=issued" />
        <Kpi label="Orders awaiting approval" value={count(f.pipeline.ordersAwaiting)} tone="primary" href="#/orders?status=draft" />
        <Kpi label="Orders open" value={count(f.pipeline.ordersOpen)} tone="good" note="approved, still to supply" href="#/orders?outstanding=1" />
        <Kpi label="Tenders open" value={count(f.pipeline.tendersOpen)} href="#/tenders?live=1" />
        <Kpi label="Closing this week" value={count(f.pipeline.tendersClosingSoon)} tone={f.pipeline.tendersClosingSoon > 0 ? 'danger' : 'empty'} href="#/tenders?live=1" />
      </Band>
      {f.money.shown ? (
        <Band title="Customers to talk to">
          <Kpi label="Accounts owing" value={count(f.money.accountsOwing)} tone="money" note={money(f.money.outstandingPaise)} href="#/billing" />
          <Kpi label="Overdue" value={money(f.money.overduePaise)} tone={f.money.overduePaise > 0 ? 'danger' : 'empty'} href="#/billing" />
          <Kpi label="Over credit limit" value={count(f.pipeline.customersOnHold)} tone={f.pipeline.customersOnHold > 0 ? 'danger' : 'empty'} href="#/billing" />
          {f.cylinders.shown ? <Kpi label="Cylinders overdue return" value={count(f.cylinders.overdueReturn)} tone={f.cylinders.overdueReturn > 0 ? 'danger' : 'empty'} href="#/billing" /> : null}
        </Band>
      ) : null}
      <div className="grid three" style={{ marginTop: 12 }}>
        <Card title="Customers" sub="setup, addresses, contacts, rates"><a className="row-link" href="#/customers" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/customers'); }}><span>Open the customer list</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
        <Card title="Rates" sub="who is on which price list"><a className="row-link" href="#/prices" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/prices'); }}><span>Open rates</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
        <Card title="Tenders" sub="notice to award"><a className="row-link" href="#/tenders" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/tenders'); }}><span>Open the tender register</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
      </div>
    </Frame>
  );
}

/** Accountant — the money, and what waits for reconciliation. */
export function AccountsHome({ session }: { session: Session }) {
  const asked = useFigures();
  if (asked.loading) return <Frame title="Accounts overview"><Loading what="the money" /></Frame>;
  if (asked.error) return <Frame title="Accounts overview"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Frame>;
  const f: Figures = asked.data!;
  const actions = can(session, 'payment.record') ? <button className="btn primary" onClick={() => go('#/billing')}>Take a payment</button> : null;
  return (
    <Frame title="Accounts overview" sub={`${greeting()} · ${longDate()}`} actions={actions}>
      <HealthBoard groups={['Billing']} title="What is wrong with the money" />
      <Band title="Money">
        <Kpi label="Received today" value={money(f.money.collectionsTodayPaise)} tone="good" href="#/billing" />
        <Kpi label="Received this month" value={money(f.money.collectionsMonthPaise)} tone="good" href="#/billing" />
        <Kpi label="Unallocated" value={money(f.money.unallocatedPaise)} tone={f.money.unallocatedPaise > 0 ? 'money' : 'empty'} note="payments not yet set against a bill" href="#/billing" />
        <Kpi label="Outstanding" value={money(f.money.outstandingPaise)} tone="money" note={`${count(f.money.accountsOwing)} accounts`} href="#/billing" />
        <Kpi label="Overdue" value={money(f.money.overduePaise)} tone={f.money.overduePaise > 0 ? 'danger' : 'empty'} href="#/billing" />
        <Kpi label="SD held" value={money(f.money.sdHeldPaise)} note="returnable when nothing is owed" href="#/billing" />
        <Kpi label="Billed this month" value={money(f.money.salesMonthPaise)} href="#/reports" />
      </Band>
      {f.cylinders.shown ? (
        <Band title="Holdings">
          <Kpi label="Cylinders with customers" value={count(f.cylinders.withCustomers)} href="#/billing" />
          <Kpi label="Overdue return" value={count(f.cylinders.overdueReturn)} tone={f.cylinders.overdueReturn > 0 ? 'danger' : 'empty'} note="holding charges accrue" href="#/billing" />
        </Band>
      ) : null}
      <div className="grid three" style={{ marginTop: 12 }}>
        <Card title="Customer accounts" sub="who owes, ageing, statements"><a className="row-link" href="#/billing" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/billing'); }}><span>Open the ageing list</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
        <Card title="Purchases" sub="supplier bills and receipts"><a className="row-link" href="#/purchases" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/purchases'); }}><span>Open purchases</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
        <Card title="Periods" sub="months open and closed"><a className="row-link" href="#/company" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/company'); }}><span>Open the calendar</span><span className="chev" aria-hidden="true">&rsaquo;</span></a></Card>
      </div>
    </Frame>
  );
}

/** Operator — large actions, scan-first, nothing financial. */
export function OperatorHome({ session }: { session: Session }) {
  const asked = useFigures();
  const f = asked.data;
  const big = (label: string, sub: string, href: string, fn: string, tone = '') => can(session, fn) ? (
    <a key={href} className={`big-action ${tone}`} href={href} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(href); }}>
      <strong>{label}</strong><span>{sub}</span>
    </a>
  ) : null;
  return (
    <Frame title="Today" sub={`${greeting()} · ${longDate()}`}>
      <HealthBoard groups={['Filling', 'Compliance']} title="What needs doing in the plant" />
      <div className="big-actions">
        {big('Dispatch cylinders', f ? `${count(f.ops.toDepart)} ready to leave` : 'scan and send out', '#/move?dir=out', 'challan.create', 'out')}
        {big('Receive returns', f ? `${count(f.ops.awaitingDelivery)} loads out` : 'scan what came back', '#/move?dir=in', 'challan.create', 'in')}
        {big('Fill cylinders', f ? `${count(f.ops.toFill)} waiting` : 'the filling floor', '#/filling', 'fill.record')}
        {big('Customer cylinders', f ? `${count(f.cylinders.customerOwnedOnSite)} on site` : 'receive, fill, return', '#/customers?tab=jobs', 'job.receive')}
        {big('Inspect a cylinder', f ? `${count(f.cylinders.notInspected)} not inspected` : 'record what you see', '#/filling?tab=inspect', 'cylinder.inspect')}
        {big('Find a cylinder', 'scan or type a serial', '#/find', '')}
        {big('Receive stock', 'what the supplier sent', '#/purchases', 'receipt.record')}
        {big('Papers', 'reprint a challan or slip', '#/move?tab=papers', 'document.read')}
      </div>
      {asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /> : null}
      {f && f.cylinders.shown && (f.cylinders.overdueTest > 0 || f.cylinders.quarantined > 0) ? (
        <Banner kind="warn" title={`${count(f.cylinders.overdueTest)} overdue for test and ${count(f.cylinders.quarantined)} quarantined — these must not be filled or sent.`} />
      ) : null}
    </Frame>
  );
}
